import React, { createContext, useContext, useEffect, useState } from "react";

const SelectionContext = createContext();

export const SelectionProvider = ({ children }) => {
    // Structure: { namespace: { page: [items...] } }
    const [selections, setSelections] = useState({});

    // Load from localStorage on mount
    useEffect(() => {
        const saved = localStorage.getItem("multiPageSelections");
        if (saved) {
            setSelections(JSON.parse(saved));
        }
    }, []);

    // Persist to localStorage on change
    useEffect(() => {
        localStorage.setItem("multiPageSelections", JSON.stringify(selections));
    }, [selections]);

    // ✅ Save selection for a namespace + page
    const savePageSelection = (namespace, page, selectedIds) => {
        setSelections((prev) => ({
            ...prev,
            [namespace]: {
                ...(prev[namespace] || {}),
                [page]: selectedIds,
            },
        }));
    };

    // ✅ Get selections for a namespace + page
    const getPageSelection = (namespace, page) => {
        return selections[namespace]?.[page] || [];
    };

    // ✅ Get all selections for a namespace (all pages merged)
    const getAllSelections = (namespace) => {
        if (!selections[namespace]) return [];
        return Object.values(selections[namespace]).flat();
    };

    // ✅ Clear selections for one namespace
    const clearNamespaceSelections = (namespace) => {
        setSelections((prev) => {
            const updated = { ...prev };
            delete updated[namespace];
            return updated;
        });
    };

    // ✅ Clear all selections across namespaces
    const clearAllSelections = () => {
        setSelections({});
        localStorage.removeItem("multiPageSelections");
    };

    return (
        <SelectionContext.Provider
            value={{
                selections,
                savePageSelection,
                getPageSelection,
                getAllSelections,
                clearNamespaceSelections,
                clearAllSelections,
            }}
        >
            {children}
        </SelectionContext.Provider>
    );
};

// Custom hook
export const useSelection = () => useContext(SelectionContext);
